import { Patient } from "../../../lib/types/patient"
import { format } from "date-fns"
import { useClinicSettings } from "../../../lib/store/clinic-settings"

interface PrintableRecordProps {
  patient: Patient;
  sections: string[];
}

export function PrintableRecord({ patient, sections }: PrintableRecordProps) {
  const { clinicName } = useClinicSettings()

  return (
    <div className="max-w-4xl mx-auto p-8 bg-background-light-base text-background-light-primary print:bg-white print:text-black dark:bg-background-dark-base dark:text-background-dark-primary print:dark:bg-white print:dark:text-black">
      <div className="text-center mb-8 print:mb-4">
        {clinicName && (
          <h1 className="text-3xl font-bold mb-4 print:text-black">{clinicName}</h1>
        )}
        <h2 className="text-2xl font-bold print:text-black">Medical Record</h2>
        <p className="text-text-light-secondary dark:text-text-dark-secondary print:text-gray-600">Generated on {format(new Date(), "MMMM d, yyyy")}</p>
      </div>

      {sections.includes("demographics") && (
        <section className="mb-8 print:mb-4">
          <h2 className="text-xl font-semibold mb-4 print:mb-2 print:text-black">Patient Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-medium print:text-black">Name</p>
              <p className="print:text-black">{patient.firstName} {patient.lastName}</p>
            </div>
            <div>
              <p className="font-medium print:text-black">Patient ID</p>
              <p className="print:text-black">{patient.id}</p>
            </div>
            <div>
              <p className="font-medium print:text-black">Date of Birth</p>
              <p className="print:text-black">{format(new Date(patient.dateOfBirth), "MMMM d, yyyy")}</p>
            </div>
            <div>
              <p className="font-medium print:text-black">Gender</p>
              <p className="capitalize print:text-black">{patient.gender}</p>
            </div>
          </div>
        </section>
      )}

      {sections.includes("medical") && (
        <section className="mb-8 print:mb-4">
          <h2 className="text-xl font-semibold mb-4 print:mb-2 print:text-black">Medical History</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2 print:text-black">Conditions</h3>
              <ul className="list-disc list-inside print:text-black">
                {patient.medicalHistory}
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-2 print:text-black">Allergies</h3>
              <ul className="list-disc list-inside print:text-black">
                {patient.allergies}
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-2 print:text-black">Current Medications</h3>
              <ul className="list-disc list-inside print:text-black">
                {patient.medications.map((med, i) => (
                  <li key={i}>
                    {med.name} - {med.dosage} ({med.frequency})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {sections.includes("insurance") && (
        <section className="mb-8 print:mb-4">
          <h2 className="text-xl font-semibold mb-4 print:mb-2 print:text-black">Insurance Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="font-medium print:text-black">Provider</p>
              <p className="print:text-black">{patient.insuranceInfo.provider}</p>
            </div>
            <div>
              <p className="font-medium print:text-black">Policy Number</p>
              <p className="print:text-black">{patient.insuranceInfo.policyNumber}</p>
            </div>
            {patient.insuranceInfo.groupNumber && (
              <div>
                <p className="font-medium print:text-black">Group Number</p>
                <p className="print:text-black">{patient.insuranceInfo.groupNumber}</p>
              </div>
            )}
          </div>
        </section>
      )}

      <div className="text-center text-sm text-text-light-accent dark:text-text-dark-accent print:text-gray-500 mt-8 print:mt-4">
        This document is confidential and protected by HIPAA regulations.
      </div>
    </div>
  );
}
